import type { UserState, WidgetRuntimeState } from '../../types/appState';

const isUserState = (user: unknown): user is WidgetRuntimeState['user'] => {
  if (typeof user !== 'undefined') {
    return true;
  }
  if (typeof user !== 'object') {
    return false;
  }
  const { id, email, countryCode, profileImage } = user as UserState;
  return (
    typeof id === 'string' &&
    typeof email === 'string' &&
    typeof countryCode === 'string' &&
    typeof profileImage === 'string'
  );
};

const isHeaderState = (header: unknown): header is WidgetRuntimeState['header'] => {
  if (!header || typeof header !== 'object') {
    return false;
  }
  const { isEnabled, onlineAgents, hideStatusBanner, statusBanner } = header as WidgetRuntimeState['header'];
  return (
    typeof isEnabled === 'boolean' &&
    Array.isArray(onlineAgents) &&
    onlineAgents.every((agent) => typeof agent === 'string') &&
    typeof hideStatusBanner === 'boolean' &&
    (statusBanner === undefined || typeof statusBanner === 'object')
  );
};

const isWidgetSessionState = (
  widgetSession: unknown,
): widgetSession is WidgetRuntimeState['widgetSession'] => {
  if (!widgetSession || typeof widgetSession !== 'object') {
    return false;
  }
  const { sessionTabId, isSessionExpired, sessionId, navigationHistory } =
    widgetSession as WidgetRuntimeState['widgetSession'];
  return (
    typeof sessionTabId === 'string' &&
    typeof isSessionExpired === 'boolean' &&
    typeof sessionId === 'string' &&
    Array.isArray(navigationHistory) &&
    navigationHistory.every((historyItem) => typeof historyItem === 'string')
  );
};

/**
 * Type guard for WidgetRuntimeState.
 * @param preloadedState - parsed json that injected from server in the html. "ssr"
 */
export const isWidgetRuntimeState = (preloadedState: unknown): preloadedState is WidgetRuntimeState => {
  if (!preloadedState || typeof preloadedState !== 'object') {
    return false;
  }
  const { user, header, widgetSession } = preloadedState as WidgetRuntimeState;
  return isUserState(user) && isHeaderState(header) && isWidgetSessionState(widgetSession);
};
